import React from 'react';
import {Collection, CollectionData} from "types/Collection";
import {FormikHelpers} from "formik";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {useOperator} from "context/OperatorContext";
import Form, {FormBlock, FormSection, MultiSelectField, TextAreaField, TextField} from "components/Form/Form";
import {Option} from "components/Form/Select";
import IconName from "types/IconName";
import * as Yup from "yup";

// Types ----

type CollectionFormProps = {
  collection?: Collection
  onSubmit: (values: CollectionData, helpers: FormikHelpers<CollectionData>) => Promise<any>
}

// Helpers ----

async function fetchExhibitOptions(isMultiSite: boolean, search?: string | null): Promise<Array<Option>> {
  const exhibits = await ngage.exhibits.filtered({search, page: 1, per_page: 500}).then(page => page?.data ?? []);

  return exhibits.map(exhibit => {
    const siteName = exhibit.zone.site.name;
    const showSiteName = isMultiSite && !!siteName;

    return {
      type: 'exhibit',
      id: exhibit.id.toString(),
      name: exhibit.name,
      description: `${exhibit.type.name}${showSiteName ? ` (${siteName})` : ''}`,
      icon: IconName.LIBRARY,
    }
  })
}

// Component ----

function CollectionForm({collection, onSubmit}: CollectionFormProps) {
  const {termFor, isMultiSite} = useOperator();

  const {data: exhibitOptions, isLoading: exhibitOptionsLoading} = useQuery(
    ['exhibits', {page: 1, perPage: 250}],
    () => fetchExhibitOptions(isMultiSite)
  )

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(250, "Maximum character limit exceeded"),
    about: Yup.string().max(50000, "Maximum character limit exceeded"),
  })

  const initialValues: CollectionData = {
    name: collection?.name || '',
    about: collection?.about || '',
    exhibit_ids: collection?.exhibits.map(exhibit => exhibit.id.toString()) || []
  }

  return (
    <Form initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      <FormSection
        name="Key Information"
        description={`Basic details about the ${termFor('collection')}`}
        columns={6}
      >
        {/* Name */}
        <FormBlock columns={4}>
          <TextField
            name="name"
            label="Name *"
            hint="250 chars max"
            help={`The display name for this ${termFor('collection')}`}
            autoComplete="off"
            maxLength={250}
          />
        </FormBlock>

        {/* About */}
        <FormBlock columns={6}>
          <TextAreaField
            name="about"
            label="About"
            hint="Optional. 50,000 chars max."
            help={`Additional information about this ${termFor('collection')}`}
            autoComplete="off"
            rows={6}
            maxChars={50000}
          />
        </FormBlock>
      </FormSection>

      <FormSection
        name="Contents"
        description={`A list of ${termFor('exhibits')} in this ${termFor('collection')}`}
        columns={6}
      >
        {/* Exhibits */}
        <FormBlock columns={6}>
          <MultiSelectField
            name="exhibit_ids"
            label={termFor("Exhibits")}
            options={exhibitOptions ?? []}
            isLoading={exhibitOptionsLoading}
          />
        </FormBlock>
      </FormSection>
    </Form>
  )
}

export default CollectionForm;